import { Alert, Button, InputField, SelectField } from '@design-system';
import { getCurrencySymbolByCode } from '@utils/locations';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCurrency } from '../../../hooks/useCurrency';
import { useFlow } from '../../../providers/FlowProvider';
import { usePreAuthOfferCtx } from '../../../providers/PreAuthOfferProvider';

export const OfferStep = ({
  productVariantId,
}: {
  productVariantId?: string;
}) => {
  const { t } = useTranslation('product');
  const [currency] = useCurrency();
  const { goNextStep } = useFlow();

  const {
    error,
    handleMakeOffer,
    loading,
    selectedAddressId,
    selectedPaymentMethodId,
    setError,
  } = usePreAuthOfferCtx();

  const form = useForm({
    defaultValues: {
      offer: null,
      expirationPeriod: 60,
    },
  });

  const expirationPeriodOptions = [
    { key: t('drawers.offer.expirationPeriod.options.30'), value: 30 },
    { key: t('drawers.offer.expirationPeriod.options.60'), value: 60 },
    { key: t('drawers.offer.expirationPeriod.options.90'), value: 90 },
  ];

  const handleSubmit = async ({
    expirationPeriod,
    offer,
  }: {
    offer: number | null;
    expirationPeriod: number;
  }) => {
    setError(undefined);
    if (offer && expirationPeriod && productVariantId) {
      const result = await handleMakeOffer(
        offer,
        expirationPeriod,
        selectedAddressId,
        selectedPaymentMethodId,
      );
      if (result) {
        goNextStep();
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {error ? <Alert status="error">{error}</Alert> : null}
      <form
        className="flex flex-col gap-6"
        onSubmit={(ev) => void form.handleSubmit(handleSubmit)(ev)}
      >
        <InputField
          key="offer"
          {...form.register('offer', {
            required: {
              message: t('drawers.offer.input.required'),
              value: true,
            },
            min: {
              message: t('drawers.offer.input.min'),
              value: 1,
            },
          })}
          error={form.formState.errors.offer?.message}
          id="offer"
          note={t('drawers.offer.input.note')}
          prefix={
            <span className="font-medium text-sm">
              {getCurrencySymbolByCode(currency)}
            </span>
          }
          type="number"
        />
        <SelectField
          {...form.register('expirationPeriod')}
          id="expirationPeriod"
          label={t('drawers.offer.expirationPeriod.label')}
          options={expirationPeriodOptions}
        />
        <Button
          disabled={!form.formState.isValid}
          fullWidth
          label={t('drawers.offer.save')}
          loading={loading}
          type="submit"
          variant="primary"
        />
      </form>
    </div>
  );
};
